// src/components/ErrorBoundary.js
import React from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';
import styled from '@emotion/styled';

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  padding: 2rem;
  text-align: center;
`;

const ErrorTitle = styled.h1`
  font-size: 2rem;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.error};
`;

const ErrorMessage = styled.p`
  font-size: 1.1rem;
  margin-bottom: 2rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ErrorButton = styled.button`
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryDark};
  }
`;

const ErrorBoundary = Sentry.ErrorBoundary;

const ErrorFallback = ({ error, resetError }) => {
  return (
    <ErrorContainer>
      <ErrorTitle>Oops! Something went wrong</ErrorTitle>
      <ErrorMessage>
        We are sorry for the inconvenience. The error has been reported and we
        will fix it as soon as possible.
      </ErrorMessage>
      {import.meta.env.DEV && (
        <pre style={{ marginBottom: '2rem', textAlign: 'left' }}>
          {error.message}
        </pre>
      )}
      <ErrorButton onClick={resetError}>Try Again</ErrorButton>
    </ErrorContainer>
  );
};

ErrorFallback.propTypes = {
  error: PropTypes.object.isRequired,
  resetError: PropTypes.func.isRequired,
};

// A page chunk that could not be fetched is a dropped connection or a deploy,
// and lazyRoutes has already tried one reload (#630). React.lazy keeps the
// failed import, so "Try Again" has to reload the page, not just re-render.
const ChunkLoadFallback = ({ reloadPage }) => (
  <ErrorContainer>
    <ErrorTitle>This page couldn&apos;t load</ErrorTitle>
    <ErrorMessage>Check your connection and try again.</ErrorMessage>
    <ErrorButton onClick={reloadPage}>Try Again</ErrorButton>
  </ErrorContainer>
);

ChunkLoadFallback.propTypes = {
  reloadPage: PropTypes.func.isRequired,
};

const AppErrorBoundary = ({
  children,
  reloadPage = () => window.location.reload(),
}) => {
  return (
    <ErrorBoundary
      fallback={(props) =>
        props.error?.isChunkLoadError ? (
          <ChunkLoadFallback reloadPage={reloadPage} />
        ) : (
          <ErrorFallback {...props} />
        )
      }
      beforeCapture={(scope, error) => {
        scope.setTag('component', 'AppErrorBoundary');
        scope.setLevel(error?.isChunkLoadError ? 'warning' : 'error');
        scope.setContext('errorBoundary', {
          caught: true,
          component: 'AppErrorBoundary',
        });
      }}
    >
      {children}
    </ErrorBoundary>
  );
};

AppErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  reloadPage: PropTypes.func,
};

export default AppErrorBoundary;
